// src/context/AuthContext.js
// Auth provider and hook for app-wide auth state.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, writeBatch, serverTimestamp, collection, collectionGroup, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { AUTH_ENSURE_USER_DEBOUNCE_MS } from '../constants/timeouts';
import { isAdminUser, effectivePlanForUser } from '../constants/uploadLimits';
import { resolveEffectivePlan } from '../utils/planResolution';
import { STUDIO_SUBCOLLECTION_PLACEHOLDER_ID, isStudioPlaceholderDoc, ownerUidFromStudioProjectRef } from '../constants/studioProjects';
import { WORKSPACE_SEATS_ENABLED } from '../constants/seatPricing';
import {
  membershipMirrorSettingsCollection,
  membershipDocsFromSettingsSnapshot,
} from '../utils/membershipMirrorFirestore';

function workspaceRoutingUidForUser(uid) {
  return uid || null;
}
const CACHE_DISPLAY_NAME_KEY = 'starling_cached_displayName';
const CACHE_UID_KEY = 'starling_cached_uid';
/** { uid, plan } — avoids showing free-tier usage hint before billing doc loads */
const CACHE_BILLING_PLAN_KEY = 'starling_cached_billing_plan';

function readCachedPlanForUid(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(CACHE_BILLING_PLAN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.uid === uid && typeof parsed?.plan === 'string' && parsed.plan) {
      return parsed.plan;
    }
  } catch (_) {}
  return null;
}

function writeCachedPlan(uid, plan) {
  if (!uid || !plan) return;
  try {
    localStorage.setItem(CACHE_BILLING_PLAN_KEY, JSON.stringify({ uid, plan }));
  } catch (_) {}
}

/** True when the user signed in with email/password (not Google-only, etc.). */
function isEmailPasswordUser(firebaseUser) {
  if (!firebaseUser?.providerData?.length) return false;
  return firebaseUser.providerData.some((p) => p?.providerId === 'password');
}

/**
 * Email/password: only Firestore `emailCodeVerified === false` means “new signup, OTP pending”.
 * Legacy password accounts omit the field (`undefined`) — treat as verified so existing users are not gated.
 * OAuth: `undefined` or missing field stays verified unless explicitly `false`.
 */
function resolveEmailCodeVerified(firebaseUser, userData) {
  const emailPassword = isEmailPasswordUser(firebaseUser);
  if (userData == null) {
    return emailPassword ? false : true;
  }
  if (emailPassword) {
    if (userData.emailCodeVerified === false) return false;
    return true;
  }
  return userData.emailCodeVerified !== false;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Full-screen logo + spinner during explicit sign-in / sign-out (not initial session check). */
  const [authSessionOverlay, setAuthSessionOverlay] = useState(false);
  /** Sync session from Firebase — updated at the start of `onAuthStateChanged` before token refresh (React `user` lags behind). */
  const [firebaseAuthUser, setFirebaseAuthUser] = useState(() => auth.currentUser);
  const ensureUserDebounceRef = useRef(null);
  const [workspaceMembershipOwnerIds, setWorkspaceMembershipOwnerIds] = useState([]);

  useEffect(() => {
    const uid = firebaseAuthUser?.uid;
    if (!uid || !WORKSPACE_SEATS_ENABLED) {
      setWorkspaceMembershipOwnerIds([]);
      return undefined;
    }
    const ownersFromSettingsMembership = new Set();
    const ownersFromLegacyMembership = new Set();
    const ownersFromProjects = new Set();

    const publish = () => {
      setWorkspaceMembershipOwnerIds(
        Array.from(new Set([
          ...ownersFromSettingsMembership,
          ...ownersFromLegacyMembership,
          ...ownersFromProjects,
        ])),
      );
    };

    const unsubMemSettings = onSnapshot(
      membershipMirrorSettingsCollection(db, uid),
      (snap) => {
        ownersFromSettingsMembership.clear();
        for (const d of membershipDocsFromSettingsSnapshot(snap)) {
          const ownerUid = d.data()?.ownerUid || d.id;
          if (ownerUid && ownerUid !== uid) ownersFromSettingsMembership.add(ownerUid);
        }
        publish();
      },
      () => {
        ownersFromSettingsMembership.clear();
        publish();
      },
    );

    const unsubMemLegacy = onSnapshot(
      collection(db, 'users', uid, 'memberships'),
      (snap) => {
        ownersFromLegacyMembership.clear();
        for (const d of snap.docs) {
          if (d.id && d.id !== uid) ownersFromLegacyMembership.add(d.id);
        }
        publish();
      },
      () => {
        ownersFromLegacyMembership.clear();
        publish();
      },
    );

    const sharedQuery = query(
      collectionGroup(db, 'studio'),
      where('memberIds', 'array-contains', uid),
    );
    const unsubProjects = onSnapshot(
      sharedQuery,
      (snap) => {
        ownersFromProjects.clear();
        for (const projSnap of snap.docs) {
          if (isStudioPlaceholderDoc(projSnap)) continue;
          const ownerUid = ownerUidFromStudioProjectRef(projSnap.ref);
          if (ownerUid && ownerUid !== uid) ownersFromProjects.add(ownerUid);
        }
        publish();
      },
      () => {
        ownersFromProjects.clear();
        publish();
      },
    );

    return () => {
      unsubMemSettings();
      unsubMemLegacy();
      unsubProjects();
    };
  }, [firebaseAuthUser?.uid]);

  const [selfBillingSeat, setSelfBillingSeat] = useState(null);
  const [selfBillingPlan, setSelfBillingPlan] = useState(null);

  const userUid = user?.uid;
  const userAdminFlag = user?.admin;
  useEffect(() => {
    const uid = userUid;
    const userIsAdmin = isAdminUser({ admin: userAdminFlag });
    if (!uid) {
      setSelfBillingSeat(null);
      setSelfBillingPlan(null);
      return undefined;
    }
    const planRef = doc(db, 'users', uid, 'billing', 'plan');
    const unsub = onSnapshot(
      planRef,
      (snap) => {
        const data = snap.exists?.() ? snap.data() : null;
        const sub = data?.subscription ?? null;
        const resolved = resolveEffectivePlan(data);
        const billingPlan = userIsAdmin ? 'enterprise' : resolved.plan;
        setSelfBillingPlan({
          plan: billingPlan,
          planSource: userIsAdmin ? 'subscription' : resolved.source,
          manualPlanOverride: userIsAdmin ? null : (data?.manualPlanOverride ?? null),
          overrideActive: userIsAdmin ? false : resolved.overrideActive,
          overrideExpired: userIsAdmin ? false : resolved.overrideExpired,
          trialExpiresAt: userIsAdmin ? null : resolved.expiresAt,
        });
        setSelfBillingSeat({
          seatDowngradePending: !!sub?.seatDowngradePending,
          seatCap: sub?.seatCap ?? null,
          seatAddonQuantity: sub?.seatAddonQuantity ?? 0,
        });
        if (!userIsAdmin) {
          writeCachedPlan(uid, resolved.plan);
          setUser((prev) => {
            if (!prev || prev.uid !== uid) return prev;
            return {
              ...prev,
              plan: resolved.plan,
              planStartedAt:
                typeof sub?.planStartedAt === 'number' ? sub.planStartedAt : prev.planStartedAt,
            };
          });
        }
      },
      () => {
        setSelfBillingSeat(null);
        setSelfBillingPlan(null);
      },
    );
    return () => unsub();
  }, [userUid, userAdminFlag]);

  const markEmailCodeVerified = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, emailCodeVerified: true } : prev));
  }, []);

  const refreshUserBillingPlan = useCallback(async () => {
    const authUser = auth.currentUser;
    if (!authUser) return;
    try {
      await authUser.getIdToken();
      const userDoc = await getDoc(doc(db, 'users', authUser.uid));
      const userData = userDoc.exists?.() ? userDoc.data() : null;
      let plan = 'free';
      let planStartedAt = null;
      if (isAdminUser(userData)) {
        plan = 'enterprise';
      } else {
        const billingDoc = await getDoc(doc(db, 'users', authUser.uid, 'billing', 'plan'));
        if (billingDoc.exists?.()) {
          const billingData = billingDoc.data();
          const sub = billingData?.subscription;
          plan = resolveEffectivePlan(billingData).plan;
          if (typeof sub?.planStartedAt === 'number') planStartedAt = sub.planStartedAt;
        }
      }
      const emailCodeVerified = resolveEmailCodeVerified(authUser, userData);
      setUser((prev) => {
        if (!prev || prev.uid !== authUser.uid) return prev;
        return {
          ...prev,
          plan,
          planStartedAt,
          emailCodeVerified,
          admin: isAdminUser(userData) ?? false,
        };
      });
      writeCachedPlan(authUser.uid, plan);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseAuthUser(firebaseUser ?? null);
      if (firebaseUser) {
        // Await fresh token before exposing user + clearing loading so Firestore listeners attach
        // with credentials (avoids permission-denied on localhost memory cache cold load).
        void (async () => {
          try {
            await firebaseUser.getIdToken(true);
          } catch (_) {}
          const cachedPlan = readCachedPlanForUid(firebaseUser.uid);
          let emailCodeVerified = resolveEmailCodeVerified(firebaseUser, null);
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists?.()) {
              emailCodeVerified = resolveEmailCodeVerified(firebaseUser, userDoc.data());
            }
          } catch (_) {}
          // Use last known billing plan so captures hint doesn't flash "5 free" for Pro users
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || firebaseUser.email || 'Account',
            plan: cachedPlan || 'free',
            emailCodeVerified,
            admin: false,
          });
          setLoading(false);
          setAuthSessionOverlay(false);
          // Resolve true plan immediately (don't wait for ensureUser debounce)
          void refreshUserBillingPlan().catch(() => {});

          if (ensureUserDebounceRef.current) clearTimeout(ensureUserDebounceRef.current);
          const userToEnsure = firebaseUser;
          ensureUserDebounceRef.current = setTimeout(async () => {
            ensureUserDebounceRef.current = null;
            try {
              const ensureUserDocument = httpsCallable(functions, 'ensureUserDocument');
              await ensureUserDocument();
            } catch (err) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[Auth] ensureUserDocument failed:', err?.message || err);
              }
            }
            // Fallback: create subcollections from client if missing (works when callable fails/unavailable)
            const uid = userToEnsure.uid;
            const billingPlanRef = doc(db, 'users', uid, 'billing', 'plan');
            try {
              await userToEnsure.getIdToken(true);
              const planSnap = await getDoc(billingPlanRef);
              if (!planSnap.exists()) {
                const now = serverTimestamp();
                const batch = writeBatch(db);
                const userRef = doc(db, 'users', uid);
                const dn = userToEnsure.displayName?.trim?.() || '';
                batch.set(
                  userRef,
                  {
                    uid,
                    email: userToEnsure.email ?? '',
                    ...(dn ? {displayName: dn} : {}),
                    createdAt: now,
                    updatedAt: now,
                  },
                  {merge: true}
                );
                batch.set(doc(userRef, 'billing', 'plan'), {
                  userId: uid,
                  subscription: {plan: 'free', status: 'active', currentPeriodEnd: null},
                  payment: {stripeCustomerId: null},
                  createdAt: now,
                  updatedAt: now,
                }, {merge: true});
                batch.set(doc(userRef, 'billing', 'metrics'), {
                  userId: uid,
                  cancelledPlanCount: 0,
                  freeCount: 1,
                  professionalCount: 0,
                  studioCount: 0,
                  enterpriseCount: 0,
                  captureTotals: {
                    totalCaptures: 0,
                    totalFailedCaptures: 0,
                    byPlan: {
                      free: 0,
                      professional: 0,
                      studio: 0,
                      enterprise: 0,
                    },
                  },
                  projectTotals: { totalProjects: 0 },
                  storage: {
                    totalStorageBytes: 0,
                    capBytes: 25 * 1024 * 1024 * 1024,
                    schemaVersion: 2,
                  },
                  media: { totalVideoDurationSeconds: 0 },
                  createdAt: now,
                  updatedAt: now,
                }, {merge: true});
                batch.set(doc(userRef, 'settings', 'preferences'), {
                  userId: uid,
                  notificationsOn: true,
                  signOutEverywhereClickCount: 0,
                  deleteAccountAttemptCount: 0,
                  createdAt: now,
                  updatedAt: now,
                }, {merge: true});
                batch.set(doc(userRef, 'studio', STUDIO_SUBCOLLECTION_PLACEHOLDER_ID), {
                  _placeholder: true,
                  projectName: '',
                  authorizedUsers: [{ uid, role: 'owner' }],
                  memberIds: [uid],
                  captureCount: 0,
                  createdAt: now,
                  updatedAt: now,
                }, {merge: true});
                try {
                  await batch.commit();
                } catch (e) {
                  const msg = e?.message || String(e);
                  console.error('[Auth] client bootstrap failed:', msg);
                }
              }
            } catch (e) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[Auth] billing bootstrap getDoc failed:', e?.message || e);
              }
            }
            // Use displayName from Firestore (source of truth), fallback to Auth then email
            let displayName = userToEnsure.displayName || userToEnsure.email || 'Account';
            let userData = null;
            try {
              const userDoc = await getDoc(doc(db, 'users', userToEnsure.uid));
              userData = userDoc.exists?.() ? userDoc.data() : null;
              const firestoreName = userData?.displayName;
              const trimmed = firestoreName && String(firestoreName).trim();
              if (trimmed) {
                displayName = trimmed;
              }
            } catch (_) {}
            let plan = 'free';
            let planStartedAt = null;
            if (isAdminUser(userData)) {
              plan = 'enterprise';
            } else {
              try {
                const billingDoc = await getDoc(doc(db, 'users', userToEnsure.uid, 'billing', 'plan'));
                if (billingDoc.exists?.()) {
                  const billingData = billingDoc.data();
                  const sub = billingData?.subscription;
                  plan = resolveEffectivePlan(billingData).plan;
                  if (typeof sub?.planStartedAt === 'number') planStartedAt = sub.planStartedAt;
                }
              } catch (_) {}
            }
            setUser({
              uid: userToEnsure.uid,
              email: userToEnsure.email,
              displayName,
              plan,
              planStartedAt,
              emailCodeVerified: resolveEmailCodeVerified(userToEnsure, userData),
              admin: isAdminUser(userData) ?? false,
            });
            writeCachedPlan(userToEnsure.uid, plan);
            try {
              localStorage.setItem(CACHE_DISPLAY_NAME_KEY, displayName);
              localStorage.setItem(CACHE_UID_KEY, userToEnsure.uid);
            } catch (_) {}
          }, AUTH_ENSURE_USER_DEBOUNCE_MS);
        })();
      } else {
        if (ensureUserDebounceRef.current) {
          clearTimeout(ensureUserDebounceRef.current);
          ensureUserDebounceRef.current = null;
        }
        setUser(null);
        try {
          localStorage.removeItem(CACHE_DISPLAY_NAME_KEY);
          localStorage.removeItem(CACHE_UID_KEY);
          localStorage.removeItem(CACHE_BILLING_PLAN_KEY);
        } catch (_) {}
        setLoading(false);
        setAuthSessionOverlay(false);
      }
    });
    return () => {
      if (ensureUserDebounceRef.current) clearTimeout(ensureUserDebounceRef.current);
      unsubscribe();
    };
  }, [refreshUserBillingPlan]);

  useEffect(() => {
    const refreshOnEvent = () => { refreshUserBillingPlan().catch(() => {}); };
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') refreshOnEvent();
    };
    window.addEventListener('starling:refresh-user-plan', refreshOnEvent);
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => {
      window.removeEventListener('starling:refresh-user-plan', refreshOnEvent);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [refreshUserBillingPlan]);

  const enrichedUser = useMemo(() => {
    if (!user) return null;
    const workspaceRoutingUid = workspaceRoutingUidForUser(user.uid);
    const admin = isAdminUser(user) ?? false;
    let merged = {
      ...user,
      admin,
      workspaceRoutingUid,
      workspaceMembershipOwnerIds: workspaceMembershipOwnerIds,
    };
    merged.plan = admin
      ? 'enterprise'
      : (selfBillingPlan?.plan ?? effectivePlanForUser(merged));
    if (!admin && selfBillingPlan) {
      merged = {
        ...merged,
        planSource: selfBillingPlan.planSource,
        manualPlanOverride: selfBillingPlan.manualPlanOverride,
        manualTrialActive: selfBillingPlan.overrideActive,
        manualTrialExpired: selfBillingPlan.overrideExpired,
        manualTrialExpiresAt: selfBillingPlan.trialExpiresAt,
      };
    }
    if (selfBillingSeat) {
      merged = {
        ...merged,
        workspaceSeatCap: selfBillingSeat.seatCap ?? merged.workspaceSeatCap,
        workspaceSeatDowngradePending: !!selfBillingSeat.seatDowngradePending,
        workspaceSeatAddonQuantity: selfBillingSeat.seatAddonQuantity ?? 0,
      };
    }
    return merged;
  }, [user, workspaceMembershipOwnerIds, selfBillingSeat, selfBillingPlan]);

  const studioTopBarUser = useMemo(() => {
    if (enrichedUser) return enrichedUser;
    const fu = firebaseAuthUser;
    if (!fu) return null;
    const cachedPlan = readCachedPlanForUid(fu.uid);
    const pseudo = {
      uid: fu.uid,
      email: fu.email,
      displayName: fu.displayName || fu.email || 'Account',
      plan: cachedPlan || 'free',
      emailCodeVerified: resolveEmailCodeVerified(fu, null),
      admin: false,
    };
    return {
      ...pseudo,
      workspaceRoutingUid: workspaceRoutingUidForUser(pseudo.uid),
      workspaceMembershipOwnerIds: workspaceMembershipOwnerIds,
    };
  }, [enrichedUser, firebaseAuthUser, workspaceMembershipOwnerIds]);

  const value = {
    user: enrichedUser,
    loading,
    authSessionOverlay,
    setAuthSessionOverlay,
    firebaseAuthUser,
    studioTopBarUser,
    refreshUserBillingPlan,
    markEmailCodeVerified,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
